class FileCacheService {
    constructor() {
        this.dbName = 'ApolloFileCache';
        this.dbVersion = 1;
        this.storeName = 'files';
        this.db = null;
        this.init();
    }

    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, { keyPath: 'url' });
                    store.createIndex('expiresAt', 'expiresAt', { unique: false });
                    store.createIndex('fileType', 'fileType', { unique: false });
                }
            };
        });
    }

    getCacheConfig(fileType) {
        const configs = {
            image: {
                maxAge: 30 * 24 * 60 * 60 * 1000, // 30 dias
                maxSize: 500 * 1024 * 1024 // 500MB
            },
            video: {
                maxAge: 1 * 24 * 60 * 60 * 1000, // 1 dia
                maxSize: 5 * 1024 * 1024 * 1024 // 5GB
            },
            raw: {
                maxAge: 30 * 24 * 60 * 60 * 1000, // 30 dias
                maxSize: 2 * 1024 * 1024 * 1024 // 2GB
            }
        };
        return configs[fileType] || configs.raw;
    }

    async getFromCache(url) {
        if (!this.db) return null;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.get(url);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    }

    async saveToCache(url, blob, fileType) {
        if (!this.db) return null;

        const config = this.getCacheConfig(fileType);
        const expiresAt = Date.now() + config.maxAge;

        const cacheEntry = {
            url,
            blob,
            fileType,
            expiresAt,
            createdAt: Date.now()
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.put(cacheEntry);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.cleanupIfNeeded();
                resolve();
            };
        });
    }

    isExpired(cacheEntry) {
        return Date.now() > cacheEntry.expiresAt;
    }

    async downloadAndCache(url, fileType) {
        try {
            console.log('🌐 Cache: Iniciando download:', url);
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            console.log('💾 Cache: Salvando blob de', blob.size, 'bytes');
            await this.saveToCache(url, blob, fileType);

            const objectUrl = URL.createObjectURL(blob);
            console.log('🔗 Cache: URL criada:', objectUrl);
            return objectUrl;
        } catch (error) {
            console.warn('❌ Cache download failed:', error);
            return null;
        }
    }

    async cleanupIfNeeded() {
        if (!this.db) return;

        try {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const index = store.index('expiresAt');

            const expiredRequest = index.openCursor(IDBKeyRange.upperBound(Date.now()));

            expiredRequest.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                }
            };
        } catch (error) {
            console.warn('Cache cleanup failed:', error);
        }
    }

    async getCachedUrl(cloudinaryUrl, fileType) {
        try {
            console.log('🔍 Cache: Procurando por:', cloudinaryUrl);

            const cached = await this.getFromCache(cloudinaryUrl);

            if (cached && !this.isExpired(cached)) {
                console.log('✅ Cache: Encontrado em cache:', cloudinaryUrl);
                return URL.createObjectURL(cached.blob);
            }

            if (cached && this.isExpired(cached)) {
                console.log('⏰ Cache: Arquivo expirado, removendo:', cloudinaryUrl);
                // Remove expired entry
                const transaction = this.db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                store.delete(cloudinaryUrl);
            }

            console.log('⬇️ Cache: Baixando e cacheados:', cloudinaryUrl);
            // Download and cache
            return await this.downloadAndCache(cloudinaryUrl, fileType);
        } catch (error) {
            console.warn('❌ Cache operation failed:', error);
            return null;
        }
    }

    async clearCache() {
        if (!this.db) return;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.clear();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }
}

// Singleton instance
const fileCacheService = new FileCacheService();

// Limpeza automática do cache a cada 24 horas
setInterval(() => {
    fileCacheService.cleanupIfNeeded();
}, 24 * 60 * 60 * 1000);

// Limpeza inicial
fileCacheService.cleanupIfNeeded();

export default fileCacheService;
